import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../shell/Can.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { getUser, USER_STATUS_LABELS, type User } from '../../api/usersApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

function fullName(u: User): string {
  return [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' ');
}

export function UserDetailPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<User | null>(null);
  const [manager, setManager] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getUser(id!, token)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  // The row stores only the manager's id, which names nobody on screen.
  useEffect(() => {
    let alive = true;
    setManager(null);
    if (!data?.managerId) return;
    getUser(data.managerId, token)
      .then((m) => { if (alive) setManager(m); })
      .catch(() => { if (alive) setManager(null); });
    return () => { alive = false; };
  }, [data?.managerId, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('users.detail.loading')}</p>;
  return (
    <main aria-label="user-detail">
      <h1>{data.displayName}</h1>
      <Can path="/admin/users" action="edit">
        <Link to={`/admin/users/${data.userId}/edit`}>{t('users.detail.edit')}</Link>
      </Can>
      <dl>
        <dt>{t('users.detail.employeeCode')}</dt><dd>{data.employeeCode ?? '—'}</dd>
        <dt>{t('users.detail.fullName')}</dt><dd>{fullName(data)}</dd>
        <dt>{t('users.detail.email')}</dt><dd>{data.emailAddress}</dd>
        <dt>{t('users.detail.mobile')}</dt><dd>{data.mobileNumber}</dd>
        <dt>{t('users.detail.userType')}</dt><dd>{data.userType}</dd>
        <dt>{t('users.detail.department')}</dt><dd>{data.department ?? '—'}</dd>
        <dt>{t('users.detail.designation')}</dt><dd>{data.designation ?? '—'}</dd>
        <dt>{t('users.create.reportingManager')}</dt>
        <dd data-testid="detail-reporting-manager">
          {!data.managerId
            ? '—'
            : manager
              ? `${manager.displayName}${manager.employeeCode ? ` (${manager.employeeCode})` : ''}`
              : data.managerId}
        </dd>
        <dt>{t('users.detail.status')}</dt><dd>{USER_STATUS_LABELS[data.userStatus] ?? data.userStatus}</dd>
        <dt>{t('users.detail.effectiveFrom')}</dt><dd>{day(data.effectiveFromDate)}</dd>
        <dt>{t('users.detail.effectiveTo')}</dt><dd>{day(data.effectiveToDate)}</dd>
      </dl>
    </main>
  );
}
